// T3-B sponsored motif (PR 4, 2026-05-07):
// sponsored_campaigns 管理画面 — list + filter (status) + create/edit sheet。
// edit sheet 内に motif 一覧 + form を sub-section として埋め込み (= spec §6.1 詳細画面)。
//
// auth: ADMIN only。
//
// Spec: design/sponsored-motif-spec.md §6.1 / §6.2

import { Button } from '@/components/common/Button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/common/Select';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Pencil, Trash2 } from 'lucide-react';
import SponsoredCampaignFormSheet from './components/SponsoredCampaignFormSheet';
import StatsSheet from './components/StatsSheet';

export const CAMPAIGN_TYPE_LABEL: Record<string, string> = {
    season: 'シーズン',
    series: 'シリーズ',
    mega: 'メガ'
};

export const CAMPAIGN_STATUS_LABEL: Record<string, string> = {
    draft: '下書き',
    active: '配布中',
    paused: '一時停止',
    ended: '終了'
};

export const STATUS_OPTIONS = [
    { value: 'all', label: 'すべて' },
    { value: 'draft', label: '下書き' },
    { value: 'active', label: '配布中' },
    { value: 'paused', label: '一時停止' },
    { value: 'ended', label: '終了' }
];

const formatDate = (iso?: string | null): string => {
    if (!iso) return '-';
    try {
        return new Date(iso).toLocaleDateString('ja-JP');
    } catch {
        return iso;
    }
};

const formatYen = (n?: number | null): string => {
    if (n == null) return '-';
    return `¥${Number(n).toLocaleString('ja-JP')}`;
};

export default observer(function SponsoredCampaignManagementPage() {
    const { t } = useTranslation();
    const {
        sponsoredCampaignStore: { campaigns, total, list: fetchList, remove: removeCampaign, changeStatus },
        sponsorStore: { list: fetchSponsors },
        modalStore: { showAlertModal }
    } = useStore();

    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetMode, setSheetMode] = useState<'create' | 'edit'>('create');
    const [editTarget, setEditTarget] = useState<any | null>(null);
    // T3-B (PR 7-CMS, 2026-05-07): 効果計測 sheet
    const [statsSheetOpen, setStatsSheetOpen] = useState(false);
    const [statsTargetId, setStatsTargetId] = useState<number | null>(null);

    const reload = () => {
        const params = statusFilter === 'all' ? {} : { status: statusFilter };
        fetchList(params);
    };

    useEffect(() => {
        // sponsor list は form sheet の select で使うので並行 fetch
        fetchSponsors();
    }, []);

    useEffect(() => {
        reload();
    }, [statusFilter]);

    const handleOpenCreate = () => {
        setSheetMode('create');
        setEditTarget(null);
        setSheetOpen(true);
    };

    const handleOpenEdit = (c: any) => {
        setSheetMode('edit');
        setEditTarget(c);
        setSheetOpen(true);
    };

    const handleDelete = (id: number, name: string, status: string) => {
        if (status !== 'draft') {
            toastify('error', '下書きのみ削除可能です。配布中・終了の場合は status を ended に変更してください。');
            return;
        }
        showAlertModal({
            title: t('words_title.confirm_delete') || '削除確認',
            size: 'sm',
            content: `「${name}」を削除しますか? (下書きのため物理削除されます)`,
            onSave: async () => {
                const res = await flowResult(removeCampaign(id));
                if (res?.ok) {
                    toastify('success', '削除しました');
                    reload();
                } else {
                    toastify('error', res?.message || '削除に失敗しました');
                }
            }
        });
    };

    const handleStatusChange = async (id: number, newStatus: string) => {
        const res = await flowResult(changeStatus({ id, status: newStatus }));
        if (res?.ok) {
            toastify('success', `状態を ${CAMPAIGN_STATUS_LABEL[newStatus]} に変更しました`);
            reload();
        } else {
            toastify('error', res?.message || '状態変更に失敗しました');
        }
    };

    return (
        <div className='layout-card'>
            <div className='flex items-center justify-between mb-4'>
                <div>
                    <h2 className='text-[18px] font-bold'>
                        {t('words_title.sponsored_campaign_management') || 'コラボ motif キャンペーン'}
                    </h2>
                    <p className='text-sm text-gray-500 mt-1'>
                        {`登録件数: ${total}`}
                    </p>
                </div>
                <Button variant='active' onClick={handleOpenCreate}>
                    {t('button.add_new') || '新規追加'}
                </Button>
            </div>

            <div className='flex items-center gap-3 mb-4'>
                <span className='text-sm text-gray-600'>状態:</span>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className='w-[180px]'>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {STATUS_OPTIONS.map(o => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {campaigns.length === 0 ?
                (
                    <div className='py-16 text-center text-gray-500'>
                        {t('messages.no_data') || 'データがありません'}
                    </div>
                ) :
                (
                    <div className='overflow-x-auto'>
                        <table className='w-full text-sm'>
                            <thead>
                                <tr className='border-b border-gray-200 text-left'>
                                    <th className='py-2 px-3'>ID</th>
                                    <th className='py-2 px-3'>キャンペーン名</th>
                                    <th className='py-2 px-3'>タイプ</th>
                                    <th className='py-2 px-3'>期間</th>
                                    <th className='py-2 px-3 text-right'>月額</th>
                                    <th className='py-2 px-3 text-right'>制作費</th>
                                    <th className='py-2 px-3 text-right'>優先度</th>
                                    <th className='py-2 px-3'>状態</th>
                                    <th className='py-2 px-3 w-[120px]'>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campaigns.map(c => (
                                    <tr key={c.id} className='border-b border-gray-100 hover:bg-gray-50'>
                                        <td className='py-2 px-3 text-gray-500'>{c.id}</td>
                                        <td className='py-2 px-3 font-medium'>{c.name}</td>
                                        <td className='py-2 px-3'>{CAMPAIGN_TYPE_LABEL[c.campaign_type] ?? c.campaign_type}</td>
                                        <td className='py-2 px-3 text-gray-600'>
                                            {formatDate(c.start_at)}
                                            {' '}
                                            〜
                                            {formatDate(c.end_at)}
                                        </td>
                                        <td className='py-2 px-3 text-right font-mono'>{formatYen(c.contract_amount)}</td>
                                        <td className='py-2 px-3 text-right font-mono'>{formatYen(c.production_fee)}</td>
                                        <td className='py-2 px-3 text-right'>{c.priority}</td>
                                        <td className='py-2 px-3'>
                                            <Select
                                                value={c.status}
                                                onValueChange={v => handleStatusChange(c.id, v)}
                                            >
                                                <SelectTrigger className='w-[120px] h-8'>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value='draft'>下書き</SelectItem>
                                                    <SelectItem value='active'>配布中</SelectItem>
                                                    <SelectItem value='paused'>一時停止</SelectItem>
                                                    <SelectItem value='ended'>終了</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </td>
                                        <td className='py-2 px-3'>
                                            <div className='flex gap-2'>
                                                <Button
                                                    variant='outline'
                                                    size='sm'
                                                    onClick={() => {
                                                        setStatsTargetId(c.id);
                                                        setStatsSheetOpen(true);
                                                    }}
                                                    title='効果計測'
                                                >
                                                    <BarChart3 className='w-4 h-4' />
                                                </Button>
                                                <Button variant='outline' size='sm' onClick={() => handleOpenEdit(c)}>
                                                    <Pencil className='w-4 h-4' />
                                                </Button>
                                                <Button
                                                    variant='outline'
                                                    size='sm'
                                                    onClick={() => handleDelete(c.id, c.name, c.status)}
                                                    className='text-red-600 hover:text-red-800'
                                                    disabled={c.status !== 'draft'}
                                                >
                                                    <Trash2 className='w-4 h-4' />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

            <SponsoredCampaignFormSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                mode={sheetMode}
                campaign={editTarget}
                onSuccess={reload}
            />

            <StatsSheet
                open={statsSheetOpen}
                onOpenChange={setStatsSheetOpen}
                campaignId={statsTargetId}
            />
        </div>
    );
});
